// Outbox flush + coaching pull (idea.md section 5). Drains pending sessions to the
// Worker on reconnect (idempotent by session id) and pulls Cowork-written coaching
// back. Never throws into the UI: returns a result the caller can show.

import { getSyncConfig, isSyncConfigured, getLastCoachingPull, setLastCoachingPull, setLastSyncAt } from './config';
import { toSyncDTO, toSyncMeasurementDTO, type SyncRequest, type SyncResponse, type CoachingResponse } from './dto';
import {
  pendingOutbox,
  markOutbox,
  getSession,
  exerciseMap,
  applyCoaching,
  pendingMeasurementOutbox,
  markMeasurementOutbox,
  getMeasurement,
} from '../db/repo';

export interface FlushResult {
  configured: boolean;
  attempted: number;
  synced: number;
  failed: number;
  error: string | null;
}

export interface PullResult {
  configured: boolean;
  applied: number;
  error: string | null;
}

const MAX_BATCH = 25;

export async function flushOutbox(): Promise<FlushResult> {
  if (!isSyncConfigured()) {
    return { configured: false, attempted: 0, synced: 0, failed: 0, error: null };
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: 'offline' };
  }

  const { workerUrl, secret } = getSyncConfig();
  const queued = (await pendingOutbox()).slice(0, MAX_BATCH);
  if (queued.length === 0) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: null };
  }

  const exById = await exerciseMap();
  const dtos = [];
  for (const item of queued) {
    const session = await getSession(item.sessionId);
    if (!session) {
      // Session deleted before it synced: drop the outbox row.
      await markOutbox(item.sessionId, 'synced', null, item.attempts);
      continue;
    }
    dtos.push(toSyncDTO(session, exById));
  }
  if (dtos.length === 0) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: null };
  }

  const body: SyncRequest = { sessions: dtos };
  let res: Response;
  try {
    res = await fetch(joinUrl(workerUrl, '/sync'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-sync-secret': secret },
      body: JSON.stringify(body),
    });
  } catch (e) {
    for (const item of queued) await markOutbox(item.sessionId, 'error', String(e), item.attempts + 1);
    return { configured: true, attempted: queued.length, synced: 0, failed: queued.length, error: String(e) };
  }

  if (!res.ok) {
    const text = await safeText(res);
    for (const item of queued) await markOutbox(item.sessionId, 'error', `HTTP ${res.status}: ${text}`, item.attempts + 1);
    return { configured: true, attempted: queued.length, synced: 0, failed: queued.length, error: `HTTP ${res.status}` };
  }

  const data = (await res.json()) as SyncResponse;
  const errorIds = new Set(data.errors?.map((e) => e.sessionId) ?? []);
  let synced = 0;
  let failed = 0;
  for (const item of queued) {
    if (errorIds.has(item.sessionId)) {
      const err = data.errors.find((e) => e.sessionId === item.sessionId)?.error ?? 'unknown';
      await markOutbox(item.sessionId, 'error', err, item.attempts + 1);
      failed++;
    } else {
      await markOutbox(item.sessionId, 'synced', null, item.attempts + 1);
      synced++;
    }
  }
  return { configured: true, attempted: queued.length, synced, failed, error: null };
}

// Drain pending body measurements to the Worker. Same mechanics as flushOutbox but
// against the measurement outbox; the Worker upserts into Notion by date so resending
// an edited measurement updates the same row.
export async function flushMeasurements(): Promise<FlushResult> {
  if (!isSyncConfigured()) {
    return { configured: false, attempted: 0, synced: 0, failed: 0, error: null };
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: 'offline' };
  }

  const { workerUrl, secret } = getSyncConfig();
  const queued = (await pendingMeasurementOutbox()).slice(0, MAX_BATCH);
  if (queued.length === 0) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: null };
  }

  const dtos = [];
  for (const item of queued) {
    const m = await getMeasurement(item.measurementId);
    if (!m) {
      // Deleted before it synced: drop the outbox row.
      await markMeasurementOutbox(item.measurementId, 'synced', null, item.attempts);
      continue;
    }
    dtos.push(toSyncMeasurementDTO(m));
  }
  if (dtos.length === 0) {
    return { configured: true, attempted: 0, synced: 0, failed: 0, error: null };
  }

  const body: SyncRequest = { measurements: dtos };
  let res: Response;
  try {
    res = await fetch(joinUrl(workerUrl, '/sync'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'x-sync-secret': secret },
      body: JSON.stringify(body),
    });
  } catch (e) {
    for (const item of queued) await markMeasurementOutbox(item.measurementId, 'error', String(e), item.attempts + 1);
    return { configured: true, attempted: queued.length, synced: 0, failed: queued.length, error: String(e) };
  }

  if (!res.ok) {
    const text = await safeText(res);
    for (const item of queued) await markMeasurementOutbox(item.measurementId, 'error', `HTTP ${res.status}: ${text}`, item.attempts + 1);
    return { configured: true, attempted: queued.length, synced: 0, failed: queued.length, error: `HTTP ${res.status}` };
  }

  const data = (await res.json()) as SyncResponse;
  const errorIds = new Set(data.measurementErrors?.map((e) => e.measurementId) ?? []);
  let synced = 0;
  let failed = 0;
  for (const item of queued) {
    if (errorIds.has(item.measurementId)) {
      const err = data.measurementErrors?.find((e) => e.measurementId === item.measurementId)?.error ?? 'unknown';
      await markMeasurementOutbox(item.measurementId, 'error', err, item.attempts + 1);
      failed++;
    } else {
      await markMeasurementOutbox(item.measurementId, 'synced', null, item.attempts + 1);
      synced++;
    }
  }
  return { configured: true, attempted: queued.length, synced, failed, error: null };
}

export async function pullCoaching(): Promise<PullResult> {
  if (!isSyncConfigured()) return { configured: false, applied: 0, error: null };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { configured: true, applied: 0, error: 'offline' };
  }

  const { workerUrl, secret } = getSyncConfig();
  const since = await getLastCoachingPull();
  const url = new URL(joinUrl(workerUrl, '/coaching'));
  if (since) url.searchParams.set('since', since);

  let res: Response;
  try {
    res = await fetch(url.toString(), { headers: { 'x-sync-secret': secret } });
  } catch (e) {
    return { configured: true, applied: 0, error: String(e) };
  }
  if (!res.ok) return { configured: true, applied: 0, error: `HTTP ${res.status}` };

  const data = (await res.json()) as CoachingResponse;
  let applied = 0;
  for (const c of data.coaching ?? []) {
    await applyCoaching(c.sessionId, c.coaching, c.suggestedAdjustments, c.coachedAt);
    applied++;
  }
  await setLastCoachingPull(new Date().toISOString());
  return { configured: true, applied, error: null };
}

// Flush then pull. Called on app open, on reconnect, and from the manual Sync button.
// Sessions and measurements drain independently; both share the Worker URL + secret.
export async function syncNow(): Promise<{ flush: FlushResult; measurements: FlushResult; pull: PullResult }> {
  const flush = await flushOutbox();
  const measurements = await flushMeasurements();
  const pull = await pullCoaching();
  // Stamp the last successful sync only when we actually reached the Worker with no
  // errors on any leg (configured + online + clean). Offline/error legs leave the
  // previous timestamp intact so the UI shows the last time data really landed.
  const ok = flush.configured && flush.error == null && measurements.error == null && pull.error == null;
  if (ok) await setLastSyncAt(new Date().toISOString());
  return { flush, measurements, pull };
}

function joinUrl(base: string, path: string): string {
  return base.replace(/\/+$/, '') + path;
}

async function safeText(res: Response): Promise<string> {
  try {
    return (await res.text()).slice(0, 200);
  } catch {
    return '';
  }
}
